import { useDialogContext } from '@app/components/dialogs/DialogProvider';
import useMediaQuery from '@mui/material/useMediaQuery';
import { theme } from '@utils/libs/theme';
import CreateTeamDialog from '@widgets/teams/CreateTeamDialog';
import ExportDialog from '@widgets/content/ExportDialog';
import ChangePasswordDialog from '@widgets/profile/password/ChangePasswordDialog';
import CreateCardDialog from '@widgets/cards/CreateCardDialog';
import EditEmailDialog from '@widgets/profile/EditEmailDialog';
import IncreaseLimitsDialog from '@widgets/content/IncreaseLimitsDialog';
import StartVerificationDialog from '@widgets/dashboard/StartVerificationDialog';
import TopUpBalanceDialog from '@widgets/balance/TopUpBalanceDialog';
import { helpContent } from '@shared/model/helpContent';
import EditCardDialog from '@widgets/cards/EditCardDialog';
import ChangeCardOwnerDialog from '@widgets/cards/ChangeCardOwnerDialog';
import FreezeCardDialog from '@widgets/cards/FreezeCardDialog';
import ArchiveCardDialog from '@widgets/cards/ArchiveCardDialog';
import BlockCardDialog from '@widgets/cards/BlockCardDialog';
import DeleteCardDialog from '@widgets/cards/DeleteCardDialog';
import UnfreezeCardDialog from '@widgets/cards/UnfreezeCardDialog';
import ChangeLimitsCardDialog from '@widgets/cards/ChangeLimitsCardDialog';
import AddChannelDialog from '@widgets/profile/AddChannelDialog';
import DeleteChannelDialog from '@widgets/profile/DeleteChannelDialog';
import EnableTwofaDialog from '@widgets/profile/EnableTwofaDialog';
import NewMemberDialog from '@widgets/teams/NewMemberDialog';
import ViewChartDialog from '@widgets/teams/ViewChartDialog';
import FreezeTeamDialog from '@widgets/teams/FreezeTeamDialog';
import DeleteTeamDialog from '@widgets/teams/DeleteTeamDialog';
import EditMemberDialog from '@widgets/members/EditMemberDialog';
import FreezeMemberDialog from '@widgets/members/FreezeMemberDialog';
import ChangeRoleDialog from '@widgets/members/ChangeRoleDialog';
import MoveMemberDialog from '@widgets/members/MoveMemberDialog';
import ViewCardDialog from '@widgets/cards/ViewCardDialog';
import TopUpOrderDialog from '@widgets/balance/TopUpOrderDialog';
import TransferDialog from '@widgets/cards/TransferDialog';
import WithdrawDialog from '@widgets/cards/WithdrawDialog';
import PrivateBinsDialog from '@widgets/dashboard/PrivateBinsDialog';
import LoginTwofaDialog from '@widgets/profile/LoginTwofaDialog';
import DisableTwofaDialog from '@widgets/profile/DisableTwofaDialog';
import UnfreezeTeamDialog from '@widgets/teams/UnfreezeTeamDialog';
import DeleteMemberDialog from '@widgets/members/DeleteMemberDialog';
import UnfreezeMemberDialog from '@widgets/members/UnfreezeMemberDialog';
import RequestTwofaDialog from '@widgets/profile/RequestTwoFaDialog';
import MoreMenuDialog from '@widgets/sidebar/MoreMenuDialog';
import MemberInfoDialog from '@widgets/members/MemberInfoDialog';
import ActiveCardsDialog from '@widgets/dashboard/ActiveCardsDialog';
import TransactionDetailsDialog from '@widgets/transactions/TransactionDetailsDialog';
import ReferralInfoDialog from '@widgets/referrals/ReferralInfoDialog';

function createDialogHook(openFnName, component, optionsOrFn = {}) {
  return function useDialog() {
    const { openModal, closeModal } = useDialogContext();

    const openDialog = (...args) => {
      const options =
        typeof optionsOrFn === 'function' ? optionsOrFn(...args) : optionsOrFn;

      openModal(component, options);
    };

    return { [openFnName]: openDialog, closeModal };
  };
}

export const useCreateTeamDialog = createDialogHook(
  'openCreateTeamDialog',
  CreateTeamDialog,
  (data) => ({
    data,
    title: 'new_team',
  }),
);

export const useExportDialog = createDialogHook(
  'openExportDialog',
  ExportDialog,
  (data) => ({
    data,
    title: 'new_team',
  }),
);

export const useChangePasswordDialog = createDialogHook(
  'openChangePasswordDialog',
  ChangePasswordDialog,
  { title: 'password_change' },
);

export const useCreateCardDialog = createDialogHook(
  'openCreateCardDialog',
  CreateCardDialog,
  (data) => ({
    data,
    title: 'new_card',
  }),
);

export const useEditEmailDialog = createDialogHook(
  'openEditEmailDialog',
  EditEmailDialog,
  { title: 'email_change' },
);

export function useHelpDialog({ page }) {
  const { openModal, closeModal } = useDialogContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const openHelpDialog = () => {
    openModal(helpContent[page].content, {
      title: helpContent[page].title,
      maxWidth: 'lg',
      bottomTransition: isMobile,
    });
  };

  return { openHelpDialog, closeModal };
}

export const usePlansDialog = createDialogHook(
  'openPlansDialog',
  IncreaseLimitsDialog,
  { title: 'tariff_plans', maxWidth: 'xl', classNames: 'increaseLimits' },
);

export const useStartVerificationDialog = createDialogHook(
  'openStartVerificationDialog',
  StartVerificationDialog,
  { title: 'verification', maxWidth: 'lg' },
);

// data — для TopUpBalanceDialogWithForm (пополнение с формой)
export const useTopUpBalanceDialog = createDialogHook(
  'openTopUpDialog',
  TopUpBalanceDialog,
  (data) => ({
    title: 'Top Up',
    data,
  }),
);

export const useEditCardDialog = createDialogHook(
  'openEditCardDialog',
  EditCardDialog,
  (data) => ({
    data,
    title: 'card_editing',
  }),
);

export const useChangeCardOwnerDialog = createDialogHook(
  'openChangeCardOwnerDialog',
  ChangeCardOwnerDialog,
  (data) => ({
    data,
    title: 'owner_change',
    maxWidth: 'lg',
  }),
);

export const useFreezeCardDialog = createDialogHook(
  'openFreezeCardDialog',
  FreezeCardDialog,
  (data) => ({
    data,
    title: 'cards_freeze',
    hideCloseButton: true,
    titleIcon: 'warning',
  }),
);

export const useArchiveCardDialog = createDialogHook(
  'openArchiveCardDialog',
  ArchiveCardDialog,
  (data) => ({
    data,
    title: 'cards_archiving',
    titleIcon: 'warning',
    hideCloseButton: true,
  }),
);

export const useBlockCardDialog = createDialogHook(
  'openBlockCardDialog',
  BlockCardDialog,
  (data) => ({
    data,
    title: 'cards_blocking',
    titleIcon: 'warning',
    hideCloseButton: true,
  }),
);

export const useDeleteCardDialog = createDialogHook(
  'openDeleteCardDialog',
  DeleteCardDialog,
  (data) => ({
    data,
    title: 'cards_deletion',
    titleIcon: 'warning',
    hideCloseButton: true,
  }),
);

export const useUnfreezeCardDialog = createDialogHook(
  'openUnfreezeCardDialog',
  UnfreezeCardDialog,
  (data) => ({
    data,
    title: 'cards_unfreezing',
    hideCloseButton: true,
    titleIcon: 'warning',
  }),
);

export const useChangeLimitsCardDialog = createDialogHook(
  'openChangeLimitsCardDialog',
  ChangeLimitsCardDialog,
  (data) => ({
    data,
    title: 'card_limits',
  }),
);

export const useAddChannelDialog = createDialogHook(
  'openAddChannelDialog',
  AddChannelDialog,
  (data, errorsResponse, type) => ({
    title: 'New withdrawal channel',
    data,
    errorsResponse,
    type,
  }),
);

export const useDeleteChannelDialog = createDialogHook(
  'openDeleteChannelDialog',
  DeleteChannelDialog,
  (data) => ({
    data,
    title: 'Deleting withdrawal channel',
    classNames: 'top-img-style',
    image: 'delete',
    hideCloseButton: true,
  }),
);

export const useEnableTwofaDialog = createDialogHook(
  'openEnableTwofaDialog',
  EnableTwofaDialog,
  { title: '2FA' },
);

export const useNewMemberDialog = createDialogHook(
  'openNewMemberDialog',
  NewMemberDialog,
  (data) => ({
    title: 'new_member',
    data,
  }),
);

export const useViewChartDialog = createDialogHook(
  'openViewChartDialog',
  ViewChartDialog,
  (data) => ({
    maxWidth: 'lg',
    data,
    type: 'team',
    title: 'chart',
    subtitle: data.name,
  }),
);

export const useViewMemberChartDialog = createDialogHook(
  'openViewMemberChartDialog',
  ViewChartDialog,
  (data) => ({
    maxWidth: 'lg',
    data,
    type: 'user',
    title: 'chart',
    subtitle: data.name,
  }),
);

export const useFreezeTeamDialog = createDialogHook(
  'openFreezeTeamDialog',
  FreezeTeamDialog,
  (data) => ({
    data,
    title: 'team_freeze',
    classNames: 'top-img-style',
    image: 'freeze',
    hideCloseButton: true,
  }),
);

export const useUnfreezeTeamDialog = createDialogHook(
  'openUnfreezeTeamDialog',
  UnfreezeTeamDialog,
  (data) => ({
    data,
    title: 'team_unfreeze',
    titleIcon: 'warning',
    hideCloseButton: true,
  }),
);

export const useDeleteTeamDialog = createDialogHook(
  'openDeleteTeamDialog',
  DeleteTeamDialog,
  (data) => ({
    data,
    title: 'team_deletion',
    classNames: 'top-img-style',
    image: 'delete',
    hideCloseButton: true,
    titleIcon: 'warning',
  }),
);

export const useEditMemberDialog = createDialogHook(
  'openEditMemberDialog',
  EditMemberDialog,
  (data) => ({
    data,
    title: '',
    maxWidth: 'lg',
    classNames: 'editMember',
  }),
);

export const useFreezeMemberDialog = createDialogHook(
  'openFreezeMemberDialog',
  FreezeMemberDialog,
  (data) => ({
    data,
    title: 'member_freeze',
    classNames: 'top-img-style',
    image: 'freeze',
    hideCloseButton: true,
  }),
);

export const useUnfreezeMemberDialog = createDialogHook(
  'openUnfreezeMemberDialog',
  UnfreezeMemberDialog,
  (data) => ({
    data,
    title: 'member_unfreeze',
    hideCloseButton: true,
    titleIcon: 'warning',
  }),
);

export const useChangeRoleDialog = createDialogHook(
  'openChangeRoleDialog',
  ChangeRoleDialog,
  (data, alreadyHasTeamLead = false) => ({
    data,
    alreadyHasTeamLead,
    classNames: 'changeRole',
    ...(alreadyHasTeamLead
      ? { title: 'change_role_error', titleIcon: 'error', maxWidth: 'xs' }
      : { title: 'change_member_role', maxWidth: 'lg' }),
  }),
);

export const useMoveMemberDialog = createDialogHook(
  'openMoveMemberDialog',
  MoveMemberDialog,
  (data) => ({
    data,
    title: 'move_member_title',
    maxWidth: 'lg',
  }),
);

export const useViewCardDialog = createDialogHook(
  'openViewCardDialog',
  ViewCardDialog,
  (data) => ({
    data,
  }),
);

export const useTopUpOrderDialog = createDialogHook(
  'openTopUpOrderDialog',
  TopUpOrderDialog,
  (data) => ({
    maxWidth: 'lg',
    data,
    title: 'top_up_order',
  }),
);

export const useTransferDialog = createDialogHook(
  'openTransferDialog',
  TransferDialog,
  (data) => ({
    title: 'transfer',
    data,
  }),
);

export const useWithdrawDialog = createDialogHook(
  'openWithdrawDialog',
  WithdrawDialog,
  (data) => ({
    title: 'withdrawal',
    data,
  }),
);

export const usePrivateBinsDialog = createDialogHook(
  'openPrivateBinsDialog',
  PrivateBinsDialog,
  { maxWidth: 'lg', title: 'private_bins' },
);

export const useLoginTwofaDialog = createDialogHook(
  'openLoginTwofaDialog',
  LoginTwofaDialog,
  (data) => ({
    title: '2FA',
    data,
  }),
);

export const useRequestTwofaDialog = createDialogHook(
  'openRequestTwofaDialog',
  RequestTwofaDialog,
  (data, backToAction, action) => ({
    title: '2FA',
    data,
    backToAction,
    action,
  }),
);

export const useDisableTwofaDialog = createDialogHook(
  'openDisableTwofaDialog',
  DisableTwofaDialog,
  { title: 'disable_2fa' },
);

export const useDeleteMemberDialog = createDialogHook(
  'openDeleteMemberDialog',
  DeleteMemberDialog,
  (data) => ({
    data,
    title: 'member_deletion',
    classNames: 'top-img-style',
    image: 'delete',
    hideCloseButton: true,
  }),
);

export const useMoreMenuDialog = createDialogHook(
  'openMoreMenuDialog',
  MoreMenuDialog,
  () => ({
    title: 'more',
    bottomTransition: true,
    classNames: 'moreMenu',
  }),
);

export const useMemberInfoDialog = createDialogHook(
  'openMemberInfoDialog',
  MemberInfoDialog,
  (data) => ({
    data,
    maxWidth: 'lg',
    classNames: 'memberInfoDialog',
  }),
);

export const useActiveCardsDialog = createDialogHook(
  'openActiveCardsDialog',
  ActiveCardsDialog,
  () => ({
    title: 'active_cards',
    maxWidth: 'md',
  }),
);

export const useTransactionDetailsDialog = createDialogHook(
  'openTransactionDetailsDialog',
  TransactionDetailsDialog,
  (data) => ({
    data,
    title: 'amount_taken',
    bottomTransition: true,
    classNames: 'transactionDetailsDialog',
  }),
);

export const useReferralInfoDialog = createDialogHook(
  'openReferralInfoDialog',
  ReferralInfoDialog,
  (data) => ({
    data,
    bottomTransition: true,
    classNames: 'referralInfoDialog',
  }),
);
